import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import confetti from 'canvas-confetti'
import { Hourglass, PartyPopper, Sparkles, Trophy } from 'lucide-react'
import TeamAvatar from '../../../team/components/TeamAvatar'
import useAudioManager from '../../../../hooks/useAudioManager'
import { hapticNotify } from '../../../../utils/haptic'
import { formatPrice } from '../../../../utils/formatters'
import useCountdown from '../../hooks/useCountdown'
import { formatEventTimer } from '../../utils/halloween'
import { CARD_CLASS, CrystalAmount, PumpkinIcon, SectionTitle } from './shared'

// Оформление открытого места по виду приза
const KIND_STYLES = {
  super: 'border-yellow-400/40 bg-gradient-to-r from-yellow-400/15 via-orange-500/10 to-transparent',
  witch: 'border-lime-400/30 bg-lime-400/[0.07]',
  consolation: 'border-white/10 bg-black/25'
}

const PlaceCard = ({ entry, large = false, onOpen }) => {
  const { t } = useTranslation()
  return (
    <motion.button
      type="button"
      layout
      initial={{ opacity: 0, scale: 0.85, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      onClick={() => onOpen(entry.team.id)}
      className={`flex w-full items-center gap-3 rounded-xl border text-left transition-transform active:scale-[0.98] ${large ? 'px-4 py-3.5' : 'px-3 py-2.5'} ${
        entry.is_mine ? 'border-orange-400/70 bg-orange-500/15 shadow-[0_0_24px_rgba(255,120,20,0.25)]' : KIND_STYLES[entry.kind] || KIND_STYLES.consolation
      }`}
    >
      <span className={`flex shrink-0 items-center justify-center rounded-lg font-black tabular-nums ${large ? 'h-9 w-9 text-base' : 'h-7 w-7 text-xs'} ${entry.kind === 'super' ? 'bg-gradient-to-br from-yellow-300 to-orange-500 text-dark-primary' : 'bg-white/10 text-white'}`}>
        {entry.place}
      </span>
      <TeamAvatar team={entry.team} size={large ? 44 : 30} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate font-bold text-white ${large ? 'text-base' : 'text-sm'}`}>{entry.team.name}</span>
        <span className="block text-[11px] text-text-secondary">{t(`halloweenEvent.draw.kinds.${entry.kind}`)}</span>
      </span>
      <CrystalAmount value={entry.amount} className={`shrink-0 font-black text-accent-primary ${large ? 'text-base' : 'text-sm'}`} />
    </motion.button>
  )
}

// Место, которое ещё не открыто (только в фазе drawing)
const HiddenPlace = ({ place, amount, large = false }) => {
  const { t } = useTranslation()
  return (
    <div className={`flex items-center gap-3 rounded-xl border border-dashed border-white/15 bg-black/30 ${large ? 'px-4 py-3.5' : 'px-3 py-2.5'}`}>
      <span className={`flex shrink-0 items-center justify-center rounded-lg bg-white/5 font-black tabular-nums text-text-secondary ${large ? 'h-9 w-9 text-base' : 'h-7 w-7 text-xs'}`}>
        {place}
      </span>
      <PumpkinIcon size={large ? 40 : 30} dim />
      <span className="min-w-0 flex-1 text-sm text-text-secondary">
        {place === 1 ? t('halloweenEvent.draw.firstLast') : t('halloweenEvent.draw.hidden')}
      </span>
      <CrystalAmount value={amount} className="shrink-0 text-sm font-bold text-text-secondary" />
    </div>
  )
}

/**
 * Экран розыгрыша и итогов. Места открываются по одному (сначала утешительные,
 * место 1 — последним); в drawing на месте суперпризов — закрытые слоты.
 * Своя банда выделяется, при её выигрыше — поздравление и конфетти один раз.
 */
const DrawBoard = ({ phase, draw, prizes, phaseEndsIn, hasTeam, onOpenTeam }) => {
  const { t } = useTranslation()
  const { playSound } = useAudioManager({ win: 'win' })
  const nextRevealIn = useCountdown(phase === 'drawing' ? draw?.next_reveal_in : null)
  const celebratedRef = useRef(false)

  const places = [...(draw?.places || [])].sort((a, b) => a.place - b.place)
  const mine = places.find((entry) => entry.is_mine)
  const byPlace = new Map(places.map((entry) => [entry.place, entry]))
  const superPrizes = [...(prizes?.super || [])].sort((a, b) => a.place - b.place)
  const others = places.filter((entry) => entry.kind !== 'super')
  const isDrawing = phase === 'drawing'
  const waitingPull = isDrawing && (draw?.next_reveal_in == null || phaseEndsIn === 0)

  useEffect(() => {
    if (!mine || celebratedRef.current) return
    celebratedRef.current = true
    confetti({
      particleCount: 160,
      spread: 85,
      origin: { y: 0.3 },
      colors: ['#FF8A1F', '#FFB347', '#7CFF4F', '#B026FF', '#00FF7F']
    })
    playSound('win')
    hapticNotify('success')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine?.place])

  // Суперпризы: в drawing показываем и закрытые слоты, в results — только разыгранные
  const superSlots = superPrizes
    .map((prize) => ({ prize, entry: byPlace.get(prize.place) }))
    .filter(({ entry }) => entry || isDrawing)

  return (
    <section className={`${CARD_CLASS} relative overflow-hidden p-5`}>
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-violet-600/20 blur-3xl" aria-hidden="true" />

      <div className="relative">
        <SectionTitle icon={<Trophy size={18} className="text-yellow-300" />}>
          {phase === 'results' ? t('halloweenEvent.draw.titleResults') : t('halloweenEvent.draw.title')}
        </SectionTitle>

        {isDrawing && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-violet-400/30 bg-violet-500/10 px-3 py-2.5">
            {waitingPull ? (
              <>
                <Sparkles size={16} className="shrink-0 animate-pulse text-violet-300" />
                <span className="text-sm font-bold text-violet-100">{t('halloweenEvent.draw.pulling')}</span>
              </>
            ) : (
              <>
                <Hourglass size={16} className="shrink-0 animate-pulse text-violet-300" />
                <span className="text-sm font-bold tabular-nums text-violet-100">
                  {t('halloweenEvent.draw.nextIn', { time: formatEventTimer(nextRevealIn, t) })}
                </span>
              </>
            )}
          </div>
        )}
        {isDrawing && <p className="mt-2 text-xs text-text-secondary">{t('halloweenEvent.draw.hint')}</p>}

        {/* Поздравление своей банде */}
        {mine && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-4 rounded-2xl border border-orange-400/50 bg-gradient-to-br from-orange-500/25 via-violet-600/15 to-lime-400/10 p-4 shadow-[0_25px_50px_-12px_rgba(255,106,0,0.35)]"
          >
            <p className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-orange-200">
              <PartyPopper size={18} />
              {t('halloweenEvent.draw.wonTitle')}
            </p>
            <p className="hev-flame-text mt-2 text-3xl font-black tabular-nums">{formatPrice(mine.amount)} 💎</p>
            <p className="mt-1 text-sm text-white/85">{t('halloweenEvent.draw.wonText', { place: mine.place })}</p>
          </motion.div>
        )}
        {!mine && phase === 'results' && hasTeam && (
          <div className="mt-4 rounded-xl border border-white/10 bg-black/30 px-4 py-3">
            <p className="text-sm font-bold text-white">{t('halloweenEvent.draw.noWinTitle')}</p>
            <p className="mt-0.5 text-xs text-text-secondary">{t('halloweenEvent.draw.noWinText')}</p>
          </div>
        )}

        {superSlots.length > 0 && (
          <div className="mt-4 space-y-2">
            {superSlots.map(({ prize, entry }) => (
              entry
                ? <PlaceCard key={`open-${prize.place}`} entry={entry} large={prize.place === 1} onOpen={onOpenTeam} />
                : <HiddenPlace key={`hidden-${prize.place}`} place={prize.place} amount={prize.amount} large={prize.place === 1} />
            ))}
          </div>
        )}

        {others.length > 0 && (
          <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <AnimatePresence initial={false}>
              {others.map((entry) => (
                <PlaceCard key={entry.place} entry={entry} onOpen={onOpenTeam} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  )
}

export default DrawBoard
